import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type {
  ConversationListItemResponse,
  ConversationStatus,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import type { ConversationThreadResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import LayoutLeftIcon from "@redotech/redo-icons/layout-left.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { Flex } from "@redotech/redo-web/flex";
import ChevronLeftIcon from "@redotech/redo-web/icon-old/chevron-left.svg";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AttentiveThreadView } from "../components/attentive-thread-view";
import { ConversationActionToolbar } from "../components/conversation-action-toolbar";
import { ConversationCardList } from "../components/conversation-card-list";
import { ConversationDetailsPanel } from "../components/conversation-details-panel";
import { ConversationHeader } from "../components/conversation-header";
import { EmailThreadView } from "../components/email-thread-view";
import { FacebookThreadView } from "../components/facebook-thread-view";
import { InstagramThreadView } from "../components/instagram-thread-view";
import { PanelResizer } from "../components/panel-resizer";
import { PostscriptThreadView } from "../components/postscript-thread-view";
import { RedoChatThreadView } from "../components/redo-chat-thread-view";
import { TiktokThreadView } from "../components/tiktok-thread-view";
import { useStatusFilter } from "../filters/filters-context";
import { useConversationView } from "../hooks/use-conversation-view";
import { useConversationViewLayout } from "../hooks/use-conversation-view-layout";
import { useMarkConversationAsRead } from "../hooks/use-mark-conversation-as-read";
import { useConversationsTableContext } from "../table/conversations-table-context";
import { ConversationStatusReadable } from "../ui/conversation-status-ui";

const ConversationCardListPanel = memo(function ConversationCardListPanel({
  onCollapse,
}: {
  onCollapse: () => void;
}) {
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    selectedConversationId,
    openConversation,
  } = useConversationsTableContext();

  const handleCardClick = useHandler(
    (conversation: ConversationListItemResponse) => {
      openConversation(conversation.id);
    },
  );

  const handleLoadMore = useHandler(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  });

  const isCardSelected = useHandler(
    (conversation: ConversationListItemResponse) => {
      return conversation.id === selectedConversationId;
    },
  );

  return (
    <ConversationCardList
      conversations={data}
      hasMore={hasNextPage}
      isCardSelected={isCardSelected}
      isFetchingMore={isFetchingNextPage}
      isLoading={isLoading}
      onCardClick={handleCardClick}
      onCollapse={onCollapse}
      onLoadMore={handleLoadMore}
    />
  );
});

interface ConversationViewProps {
  conversationId: ConversationId;
}

/**
 * Conversation view component that displays conversation data.
 * Renders threads using channel-appropriate components.
 */
export const ConversationView = memo(function ConversationView({
  conversationId,
}: ConversationViewProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const team = useRequiredContext(TeamContext);

  const { conversation, threads, isLoading, customerName, latestMessageTime } =
    useConversationView(conversationId);

  const { status } = useStatusFilter();

  const {
    leftPanelOpen,
    toggleLeftPanel,
    rightPanelOpen,
    toggleRightPanel,
    leftPanelWidth,
    rightPanelWidth,
    handleLeftResize,
    handleRightResize,
    handleLeftResizeEnd,
    handleRightResizeEnd,
  } = useConversationViewLayout();

  useMarkConversationAsRead(conversationId);

  const handleBack = useCallback(
    () => navigate(`/stores/${team._id}/support-v2${location.search}`),
    [navigate, team._id, location.search],
  );

  const conversationMatchesFilter =
    !status || !conversation || conversation.status === status;

  const statusLabel = status
    ? ConversationStatusReadable[status as ConversationStatus].toLowerCase()
    : null;

  if (isLoading) {
    return (
      <Flex align="center" justify="center" p="xl">
        <RedoSpinner />
      </Flex>
    );
  }

  if (!conversation) {
    return (
      <Flex align="center" justify="center" p="xl">
        <Text>Conversation not found</Text>
      </Flex>
    );
  }

  return (
    <Flex dir="column" gap="none" height="full" overflow="hidden" w="full">
      <Flex flex={1} gap="none" style={{ minHeight: 0 }}>
        {leftPanelOpen && (
          <>
            <Flex
              borderColor="secondary"
              borderRightWidth="1px"
              borderStyle="solid"
              dir="column"
              style={{ flex: `0 0 ${leftPanelWidth}px` }}
            >
              <ConversationCardListPanel onCollapse={toggleLeftPanel} />
            </Flex>
            <PanelResizer
              onResize={handleLeftResize}
              onResizeEnd={handleLeftResizeEnd}
            />
          </>
        )}

        <Flex dir="column" style={{ flex: 1, minWidth: 0 }}>
          {conversationMatchesFilter ? (
            <>
              <ConversationHeader
                actionToolbar={
                  <ConversationActionToolbar
                    conversation={conversation}
                    conversationId={conversationId}
                  />
                }
                customerName={customerName}
                latestMessageTime={latestMessageTime}
                leftPanelOpen={leftPanelOpen}
                onBack={handleBack}
                onToggleLeftPanel={toggleLeftPanel}
                onToggleRightPanel={toggleRightPanel}
                rightPanelOpen={rightPanelOpen}
              />
              <Flex
                bgColor="secondary"
                dir="column"
                grow={1}
                overflowY="auto"
                p="xl"
                style={{ minHeight: 0 }}
              >
                <Flex dir="column" gap="xl" grow={1}>
                  {threads.length === 0 ? (
                    <Flex align="center" grow={1} justify="center">
                      <Text fontSize="md" textColor="secondary">
                        No messages in this thread
                      </Text>
                    </Flex>
                  ) : (
                    threads.map((thread) => (
                      <ThreadRenderer key={thread.id} thread={thread} />
                    ))
                  )}
                </Flex>
              </Flex>
            </>
          ) : (
            <>
              <Flex
                align="center"
                bgColor="primary"
                borderBottomWidth="1px"
                borderColor="secondary"
                borderStyle="solid"
                gap="md"
                pl="xl"
                shrink={0}
                style={{ height: 48 }}
              >
                {!leftPanelOpen && (
                  <RedoButton
                    hierarchy="tertiary"
                    IconLeading={LayoutLeftIcon}
                    onClick={toggleLeftPanel}
                    size="sm"
                  />
                )}
                <RedoButton
                  hierarchy="secondary"
                  IconLeading={ChevronLeftIcon}
                  onClick={handleBack}
                  size="sm"
                />
                <Text fontSize="md" fontWeight="semibold" textColor="primary">
                  No ticket selected
                </Text>
              </Flex>
              <Flex
                align="center"
                bgColor="secondary"
                dir="column"
                grow={1}
                justify="center"
                style={{ minHeight: 0 }}
              >
                <Text fontSize="md" textColor="tertiary">
                  {`Select ${statusLabel ? `${/^[aeiou]/i.test(statusLabel) ? "an" : "a"} ${statusLabel}` : "a"} ticket to view details.`}
                </Text>
              </Flex>
            </>
          )}
        </Flex>

        {rightPanelOpen && conversationMatchesFilter && (
          <>
            <PanelResizer
              onResize={handleRightResize}
              onResizeEnd={handleRightResizeEnd}
            />
            <Flex
              bgColor="primary"
              borderColor="secondary"
              borderLeftWidth="1px"
              borderStyle="solid"
              dir="column"
              overflow="hidden"
              style={{ flex: `0 0 ${rightPanelWidth}px` }}
            >
              <ConversationDetailsPanel
                conversation={conversation}
                onCollapse={toggleRightPanel}
              />
            </Flex>
          </>
        )}
      </Flex>
    </Flex>
  );
});

interface ThreadRendererProps {
  thread: ConversationThreadResponse;
}

/**
 * Renders a thread using the appropriate component based on integration type.
 * This switch is exhaustive - TypeScript will error if a new integration type
 * is added without handling it here.
 */
const ThreadRenderer = memo(function ThreadRenderer({
  thread,
}: ThreadRendererProps) {
  switch (thread.integrationType) {
    case "gmail":
    case "outlook":
      return <EmailThreadView thread={thread} />;
    case "facebook":
      return <FacebookThreadView thread={thread} />;
    case "instagram":
      return <InstagramThreadView thread={thread} />;
    case "tiktok":
      return <TiktokThreadView thread={thread} />;
    case "attentive":
      return <AttentiveThreadView thread={thread} />;
    case "postscript":
      return <PostscriptThreadView thread={thread} />;
    case "redoChatAi":
    case "redoChatFlow":
    case "redoChatLive":
      return <RedoChatThreadView thread={thread} />;
    default: {
      assertNever(thread.integrationType);
    }
  }
});
